import { DOCUMENT_TYPE } from 'Dictionaries/docForm/docFormEnums';
import { uniqBy, flatten } from 'lodash/array';

export const fetchDocumentLayouts = async () => {
    let layouts = [];
    let pagination = {
        limit: 100,
        offset: '',
    };
    do {
        const response = await ApiClient.get('document-layouts', pagination);
        layouts = layouts.concat(response.data.data);
        pagination = response.data.pagination;
    } while (pagination.total > layouts.length);
    return layouts;
};

export const fetchLanguages = async () => {
    let languages = [];
    let pagination = {
        limit: 100,
        offset: '',
    };
    do {
        const response = await ApiClient.get('languages', pagination);
        languages = languages.concat(response.data.data);
        pagination = response.data.pagination;
    } while (pagination.total > languages.length);
    return languages;
};

export const getAllDocumentCustomFieldsValues = async (docType = DOCUMENT_TYPE.ESTIMATE, id) => {
    if (
        !id ||
        ![DOCUMENT_TYPE.ESTIMATE, DOCUMENT_TYPE.INVOICE, DOCUMENT_TYPE.ORDER].includes(docType)
    ) {
        return null;
    }

    let customFields = [];
    let pagination = {
        limit: 100,
        offset: '',
    };

    let endPoint = `/estimates/${id}/custom-fields`;

    if (docType === DOCUMENT_TYPE.ORDER) {
        endPoint = `/orders/${id}/custom-fields`;
    } else if (docType === DOCUMENT_TYPE.INVOICE) {
        endPoint = `/invoices/${id}/custom-fields`;
    }

    do {
        const response = await ApiClient.get(endPoint, { ...pagination, embed: ['value_details'] });
        customFields = customFields.concat(response.data.data);
        pagination = response.data.pagination;
    } while (pagination.total > customFields.length);

    return customFields;
};

export const getAllDocumentCustomFields = async () => {
    let customFields = [];
    let pagination = {
        limit: 100,
        offset: '',
    };

    do {
        const response = await ApiClient.post('/custom-fields/search', pagination, {
            filters: {
                related_object: 'document',
            },
        });
        customFields = customFields.concat(response.data.data);
        pagination = response.data.pagination;
    } while (pagination.total > customFields.length);

    return customFields;
};

export const updateDocumentCustomFields = async (id, customFields) => {
    const endpoint = `/invoices/${id}/custom-fields`;

    return ApiClient.put(endpoint, {}, customFields);
};

export const searchBarcode = async (item_id, declination_id, signal) => {
    const barcodes = [];
    const pagination = {
        limit: 100,
        offset: '',
    };
    const filters = {
        item_id,
        declination_id,
    };

    if (!declination_id) {
        delete filters.declination_id;
    }

    do {
        const response = await ApiClient.post(
            'items/barcodes/search',
            pagination,
            { filters },
            signal,
        );
        barcodes.push(...response.data.data);
        pagination.offset = response.data.pagination.offset;
    } while (pagination.total > barcodes.length);
    return barcodes.map((barcode) => ({ label: barcode.code, id: barcode.id, value: barcode.id }));
};

export const fetchPaymentList = async (docType, docId) => {
    try {
        const endpoint = getPaymentEndpoint(docType, docId);

        const paymentList = [];
        const pagination = {
            limit: 100,
            offset: '',
            order: 'paid_at',
        };

        do {
            const response = await ApiClient.get(endpoint, pagination);
            paymentList.push(...response.data.data);
            pagination.offset = response.data.pagination.offset;
        } while (pagination.total > paymentList.length);

        return uniqBy(paymentList, 'id');
    } catch (error) {
        if (error.response?.status !== 403) {
            window.sellsyapp.config.globalProperties.$store.state.system.errorsHandler.logException(
                error,
            );
        }
        return [];
    }
};

export const linkPayments = ({ parentDocType, parentDocId, docType, docId, payments }) => {
    const requests = payments.map((payment) => {
        return [
            {
                uri: `${getPaymentEndpoint(parentDocType, parentDocId)}/${payment.id}`,
                method: 'DELETE',
            },
            {},
            { uri: `${getPaymentEndpoint(docType, docId)}/${payment.id}`, method: 'POST' },
            { amount: payment.amount },
        ];
    });
    return ApiBatchClient.post('/batch', {}, flatten(requests).map(JSON.stringify).join('\n'));
};

const getPaymentEndpoint = (docType, docId) => {
    switch (docType) {
        case DOCUMENT_TYPE.ORDER:
            return `/orders/${docId}/payments`;
        case DOCUMENT_TYPE.INVOICE:
            return `/invoices/${docId}/payments`;
        case DOCUMENT_TYPE.ESTIMATE:
        default:
            return `/estimates/${docId}/payments`;
    }
};
